import React, { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Hotel,
  Landmark,
  Car,
  Factory,
  Sparkles,
  CheckCircle2,
  Briefcase,
  ShoppingBag,
  Truck,
  Plane,
  Radio,
  GraduationCap,
  Home,
  Heart,
} from "lucide-react";
import MascotOrb from "./MascotOrb";
import traineeAvatar from "@/assets/avatar.png";
import hospitalityVideo from "@/assets/Hospitality.mp4";
import bankingVideo from "@/assets/banking.mp4";
import automobileVideo from "@/assets/Automobile.mp4";
import manufacturingVideo from "@/assets/Manufacturing.mp4";

/* ────────────────────────────────────────────────────────────
 *  Industry storyboard data
 * ──────────────────────────────────────────────────────────── */
const INDUSTRIES = [
  {
    id: "hospitality",
    Icon: Hotel,
    name: "Hospitality",
    description: "Train teams to deliver exceptional guest experiences.",
    useCases: [
      "Handling difficult guests",
      "Upselling premium services",
      "Service recovery",
    ],
    scenario:
      "A guest is upset about a delayed check-in and demands compensation.",
    accent: "from-rose-500 to-amber-500",
    ring: "ring-rose-200",
    portal: {
      moduleTitle: "Guest Experience Training",
      moduleSubtitle: "Front-of-house · Concierge · Recovery",
      progressDone: 2,
      progressTotal: 5,
      featured: {
        title: "Service recovery · upset guest",
        image:
          "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&h=700&fit=crop&auto=format&q=80",
        caption:
          "A guest at the front desk is frustrated after waiting forty minutes. Let us walk through how you could acknowledge the delay, own the situation, and turn it into a memorable recovery.",
        video: hospitalityVideo,
      },
      sessions: [
        {
          title: "Welcoming arrivals",
          image:
            "https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=600&h=400&fit=crop&auto=format&q=80",
        },
        {
          title: "Managing complaints",
          image:
            "https://images.unsplash.com/photo-1551135049-8a33b5883817?w=600&h=400&fit=crop&auto=format&q=80",
        },
        {
          title: "Upselling suites",
          image:
            "https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=600&h=400&fit=crop&auto=format&q=80",
        },
        {
          title: "Recovery protocols",
          image:
            "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600&h=400&fit=crop&auto=format&q=80",
        },
      ],
    },
  },
  {
    id: "banking",
    Icon: Landmark,
    name: "Banking",
    description: "Build trust and drive high-value conversations.",
    useCases: [
      "Cross-selling products",
      "Handling sensitive financial queries",
      "Relationship building",
    ],
    scenario:
      "A client is unsure about investing and needs reassurance.",
    accent: "from-blue-500 to-indigo-500",
    ring: "ring-blue-200",
    portal: {
      moduleTitle: "Relationship Manager Training",
      moduleSubtitle: "Trust · Compliance · Advisory conversations",
      progressDone: 3,
      progressTotal: 6,
      featured: {
        title: "Advisory · hesitant investor",
        image:
          "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200&h=700&fit=crop&auto=format&q=80",
        caption:
          "Your client is worried about downside risk on a new product. Let us practice building trust with clear language, acknowledging the concern, and anchoring the conversation on long-term goals.",
        video: bankingVideo,
      },
      sessions: [
        {
          title: "First client meeting",
          image:
            "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=600&h=400&fit=crop&auto=format&q=80",
        },
        {
          title: "Cross-selling basics",
          image:
            "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&h=400&fit=crop&auto=format&q=80",
        },
        {
          title: "Sensitive conversations",
          image:
            "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&h=400&fit=crop&auto=format&q=80",
        },
        {
          title: "Portfolio review",
          image:
            "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop&auto=format&q=80",
        },
      ],
    },
  },
  {
    id: "automotive",
    Icon: Car,
    name: "Automotive",
    description: "Enable high-conversion showroom interactions.",
    useCases: [
      "Feature-based selling",
      "Handling price objections",
      "Test drive conversations",
    ],
    scenario:
      "A customer is comparing competitors and negotiating aggressively.",
    accent: "from-slate-700 to-slate-900",
    ring: "ring-slate-300",
    portal: {
      moduleTitle: "Showroom Sales Training",
      moduleSubtitle: "Discovery · Test drive · Close",
      progressDone: 1,
      progressTotal: 4,
      featured: {
        title: "Negotiation · competitor compare",
        image:
          "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1200&h=700&fit=crop&auto=format&q=80",
        caption:
          "The customer is comparing you with a competitor and pushing hard on price. Let us try a technique that re-centers the conversation on value and delivery — not just the sticker number.",
        video: automobileVideo,
      },
      sessions: [
        {
          title: "Feature discovery",
          image:
            "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=600&h=400&fit=crop&auto=format&q=80",
        },
        {
          title: "Test drive script",
          image:
            "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=600&h=400&fit=crop&auto=format&q=80",
        },
        {
          title: "Price objections",
          image:
            "https://images.unsplash.com/photo-1511919884226-fd3cad34687c?w=600&h=400&fit=crop&auto=format&q=80",
        },
        {
          title: "Closing the deal",
          image:
            "https://images.unsplash.com/photo-1549924231-f129b911e442?w=600&h=400&fit=crop&auto=format&q=80",
        },
      ],
    },
  },
  {
    id: "manufacturing",
    Icon: Factory,
    name: "Manufacturing",
    description: "Train teams for complex B2B interactions.",
    useCases: [
      "Technical product explanation",
      "Negotiation with clients",
      "Client onboarding",
    ],
    scenario:
      "A client questions product reliability and ROI before purchase.",
    accent: "from-teal-500 to-emerald-500",
    ring: "ring-teal-200",
    portal: {
      moduleTitle: "B2B Sales Training",
      moduleSubtitle: "Technical · Negotiation · Onboarding",
      progressDone: 4,
      progressTotal: 5,
      featured: {
        title: "Reliability · ROI objection",
        image:
          "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1200&h=700&fit=crop&auto=format&q=80",
        caption:
          "The procurement lead doubts the reliability claim and is asking for ROI proof before signing off. Let us rehearse anchoring the answer on operating data, warranty terms, and total cost of ownership.",
        video: manufacturingVideo,
      },
      sessions: [
        {
          title: "Technical walkthrough",
          image:
            "https://images.unsplash.com/photo-1565043589221-1a6fd9ae45c7?w=600&h=400&fit=crop&auto=format&q=80",
        },
        {
          title: "Procurement negotiation",
          image:
            "https://images.unsplash.com/photo-1573164713988-8665fc963095?w=600&h=400&fit=crop&auto=format&q=80",
        },
        {
          title: "Implementation plan",
          image:
            "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=600&h=400&fit=crop&auto=format&q=80",
        },
        {
          title: "Client onboarding",
          image:
            "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=600&h=400&fit=crop&auto=format&q=80",
        },
      ],
    },
  },
];

const MORE_INDUSTRIES = [
  { label: "Healthcare", Icon: Heart },
  { label: "Retail", Icon: ShoppingBag },
  { label: "Logistics", Icon: Truck },
  { label: "Aviation", Icon: Plane },
  { label: "Telecom", Icon: Radio },
  { label: "Education", Icon: GraduationCap },
  { label: "Real Estate", Icon: Home },
  { label: "Professional Services", Icon: Briefcase },
];

/* ────────────────────────────────────────────────────────────
 *  Section
 * ──────────────────────────────────────────────────────────── */
export default function Industries() {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrubRef = useRef(null);

  // Desktop scrub: the outer container is `INDUSTRIES.length × 100vh` tall.
  // A sticky child fills the viewport for the whole scroll. We derive
  // `activeIndex` purely from how much of the scrub container has scrolled
  // past — so a scroll of 0→25% shows Hospitality, 25→50% Banking, etc.
  // Result: content SWAPS IN PLACE. Nothing scrolls in from below.
  useEffect(() => {
    let raf = 0;

    const recompute = () => {
      const el = scrubRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const scrollable = rect.height - vh; // total scrollable distance inside the scrub container
      if (scrollable <= 0) return;

      // `-rect.top` is how many px have scrolled past the container's top once
      // it's pinned. Clamp 0..scrollable, then map to segment index.
      const scrolled = Math.max(0, Math.min(scrollable, -rect.top));
      const progress = scrolled / scrollable; // 0..1
      // Divide the scroll into N equal segments, one per industry.
      const raw = Math.floor(progress * INDUSTRIES.length);
      const idx = Math.max(0, Math.min(INDUSTRIES.length - 1, raw));
      setActiveIndex((prev) => (prev === idx ? prev : idx));
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        recompute();
      });
    };

    recompute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const active = INDUSTRIES[activeIndex];

  return (
    <section
      id="industries"
      data-testid="industries-section"
      className="relative"
      style={{
        // `overflow-x: clip` contains stray ambient orbs horizontally WITHOUT
        // breaking `position: sticky` on the left-column portal (which a
        // regular `overflow: hidden` would do by creating a new scroll context).
        overflowX: "clip",
        background:
          "linear-gradient(180deg, #ffffff 0%, #f5f7ff 30%, #f3efff 70%, #ffffff 100%)",
      }}
    >
      {/* Ambient drifting gradient */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none overflow-hidden"
      >
        <div
          className="bg-orb bg-orb--a"
          style={{
            width: 620,
            height: 620,
            top: "6%",
            left: "-12%",
            background:
              "radial-gradient(circle, rgba(129,140,248,0.24), transparent 70%)",
          }}
        />
        <div
          className="bg-orb bg-orb--b"
          style={{
            width: 560,
            height: 560,
            top: "55%",
            right: "-10%",
            background:
              "radial-gradient(circle, rgba(244,114,182,0.18), transparent 70%)",
          }}
        />
      </div>

      {/* ── Header ─────────────────────────────────────────── */}
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 sm:pt-28 text-center">
        <div
          className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-indigo-700 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm border border-indigo-100"
          data-testid="industries-eyebrow"
        >
          <Sparkles size={12} strokeWidth={2.4} />
          Built for every team
        </div>
        <h2 className="mt-5 text-[34px] sm:text-[48px] lg:text-[56px] leading-[1.04] font-extrabold tracking-tight text-slate-900 text-balance">
          One AI Trainer.{" "}
          <span
            className="bg-clip-text text-transparent"
            style={{
              backgroundImage:
                "linear-gradient(135deg, #4f46e5 0%, #a855f7 50%, #ec4899 100%)",
            }}
          >
            Every Industry.
          </span>
        </h2>
        <p className="mt-5 text-[16.5px] sm:text-[18px] leading-relaxed text-slate-600 max-w-2xl mx-auto">
          Watch how the same training adapts to real-world scenarios across
          industries.
        </p>
      </div>

      {/* ── DESKTOP: sticky scrub storyboard ────────────────── */}
      <div
        ref={scrubRef}
        className="hidden lg:block relative mt-16"
        style={{ height: `${INDUSTRIES.length * 100}vh` }}
        data-testid="industries-scrub-container"
      >
        <div className="sticky top-0 h-screen w-full flex items-center overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)] gap-14 items-center">
              {/* LEFT — Portal crossfades in place per industry */}
              <div>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={active.id + "-portal-desktop"}
                    initial={{ opacity: 0, y: 12, scale: 0.985 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.99 }}
                    transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <Portal industry={active} activeIndex={activeIndex} />
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* RIGHT — Copy crossfades in place */}
              <div>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={active.id + "-copy-desktop"}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <IndustryCopy
                      industry={active}
                      isActive
                      index={activeIndex}
                      total={INDUSTRIES.length}
                    />
                  </motion.div>
                </AnimatePresence>

                {/* Progress rail — subtle dot tracker so the user knows
                    there are more industries below. */}
                <div
                  className="mt-10 flex items-center gap-2"
                  data-testid="industries-progress-rail"
                >
                  {INDUSTRIES.map((ind, i) => (
                    <div
                      key={ind.id}
                      className={`h-[3px] rounded-full transition-all duration-500 ${
                        i === activeIndex
                          ? "w-10 bg-gradient-to-r " + ind.accent
                          : i < activeIndex
                            ? "w-5 bg-slate-300"
                            : "w-5 bg-slate-200"
                      }`}
                    />
                  ))}
                  <span className="ml-2 text-[10.5px] font-bold tracking-[0.18em] uppercase text-slate-500 tabular-nums">
                    {String(activeIndex + 1).padStart(2, "0")}
                    <span className="text-slate-300 mx-1">/</span>
                    {String(INDUSTRIES.length).padStart(2, "0")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── MOBILE: stacked single-column sections ──────────── */}
      <div className="lg:hidden relative max-w-7xl mx-auto px-4 sm:px-6 mt-12 sm:mt-16">
        {INDUSTRIES.map((ind, i) => (
          <div
            key={ind.id}
            data-testid={`industry-block-${ind.id}`}
            className="py-12 border-b border-slate-100 last:border-b-0"
          >
            <IndustryCopy
              industry={ind}
              isActive
              index={i}
              total={INDUSTRIES.length}
            />
            <div className="mt-8">
              <Portal industry={ind} activeIndex={i} compact />
            </div>
          </div>
        ))}
      </div>

      {/* ── And many more industries... ────────────────────── */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 sm:pb-28 mt-4 sm:mt-10">
        <div className="max-w-3xl mx-auto text-center">
          <h3
            className="text-[26px] sm:text-[36px] leading-[1.1] font-extrabold tracking-tight text-slate-900"
            data-testid="industries-more-heading"
          >
            And many more industries…
          </h3>
          <p className="mt-3 text-[15px] sm:text-[16px] text-slate-600 leading-relaxed">
            From healthcare to retail to logistics — train any frontline team
            with real-world scenarios.
          </p>
        </div>

        {/* Marquee */}
        <div
          className="relative mt-10 overflow-hidden"
          style={{
            maskImage:
              "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)",
          }}
          data-testid="industries-marquee"
        >
          <div className="flex gap-3 industries-marquee">
            {[...MORE_INDUSTRIES, ...MORE_INDUSTRIES].map((m, i) => {
              const Icon = m.Icon;
              return (
                <div
                  key={i}
                  className="flex items-center gap-2.5 flex-shrink-0 rounded-full bg-white/80 backdrop-blur-sm border border-slate-200 shadow-[0_8px_24px_-16px_rgba(15,23,42,0.2)] px-5 py-3"
                >
                  <span className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 text-white flex items-center justify-center">
                    <Icon size={14} strokeWidth={2.4} />
                  </span>
                  <span className="text-[14px] font-semibold text-slate-800 whitespace-nowrap">
                    {m.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────
 *  Right-column industry copy block
 * ──────────────────────────────────────────────────────────── */
function IndustryCopy({ industry, index, total }) {
  const { Icon } = industry;
  return (
    <div className="relative">
      {/* Rail + step indicator */}
      <div className="flex items-center gap-3 mb-6">
        <span
          className={`inline-flex items-center justify-center w-11 h-11 rounded-2xl text-white bg-gradient-to-br ${industry.accent} shadow-[0_14px_30px_-12px_rgba(15,23,42,0.35)]`}
        >
          <Icon size={20} strokeWidth={2.2} />
        </span>
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500 tabular-nums">
          {String(index + 1).padStart(2, "0")}
          <span className="text-slate-300 mx-1">/</span>
          {String(total).padStart(2, "0")}
          <span className="text-slate-300 mx-2">·</span>
          Industry scenario
        </div>
      </div>

      <h3 className="text-[36px] sm:text-[48px] leading-[1.04] font-extrabold tracking-tight text-slate-900">
        {industry.name}
      </h3>
      <p className="mt-3 text-[17px] text-slate-600 leading-relaxed max-w-lg">
        {industry.description}
      </p>

      {/* Use cases */}
      <ul className="mt-7 space-y-2.5 max-w-md">
        {industry.useCases.map((u) => (
          <li
            key={u}
            className="flex items-start gap-3 text-[15px] text-slate-800"
          >
            <CheckCircle2
              size={18}
              strokeWidth={2.2}
              className="mt-0.5 text-emerald-500 flex-shrink-0"
            />
            <span className="leading-snug">{u}</span>
          </li>
        ))}
      </ul>

      {/* Scenario — this is the moment */}
      <div
        className="mt-8 max-w-xl rounded-2xl bg-white/70 backdrop-blur-md border border-white shadow-[0_16px_36px_-18px_rgba(79,70,229,0.3)] p-5"
        data-testid={`industry-scenario-${industry.id}`}
      >
        <div className="flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-[0.2em] text-indigo-700">
          <span
            className="inline-block w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse"
            aria-hidden
          />
          AI simulates this scenario
        </div>
        <p className="mt-2 text-[15.5px] text-slate-900 font-medium leading-snug">
          “{industry.scenario}”
        </p>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
 *  Left-column AI Training Portal (sticky)
 *  Focus: ONE AI session per industry. Video autoplay-muted-loop.
 *  Swaps cleanly when the user scrolls the next industry into focus.
 * ──────────────────────────────────────────────────────────── */
function Portal({ industry, activeIndex, compact = false }) {
  const p = industry.portal;

  return (
    <div
      className="relative isolate"
      data-testid="industries-portal"
    >
      {/* Portal header */}
 

      {/* Featured AI session card (hero) */}
      <div className="relative z-0 mt-4">
        <FeaturedModuleCard industry={industry} />
      </div>

      {/* Module meta strip */}
      <AnimatePresence mode="wait">
        <motion.div
          key={industry.id + "-meta"}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 mt-6 rounded-2xl border border-slate-200/80 bg-white px-4 py-3 shadow-[0_8px_28px_-12px_rgba(15,23,42,0.14)]"
        >
          <div className="text-[12.5px] font-bold text-slate-900 leading-tight">
            {p.moduleTitle}
          </div>
          <div className="text-[11.5px] text-slate-500 mt-0.5">
            {p.moduleSubtitle}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

/* Featured AI session card — mascot orb overlaps corner, ONE scenario
 * video plays muted/looping per industry, trainee REC camera overlay
 * sits bottom-right like a live-session UI.
 */
function FeaturedModuleCard({ industry }) {
  const f = industry.portal.featured;
  const [orbCompact, setOrbCompact] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const sync = () => setOrbCompact(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const orbSize = orbCompact ? 64 : 92;
  const orbTop = orbCompact ? -21 : -30;
  const orbLeft = orbCompact ? -15 : -22;

  return (
    <div className="relative">
      {/* Speaking mascot — half inside, half outside (top-left overlap) */}
      <div
        className="absolute z-20 pointer-events-none flex items-center justify-center"
        style={{ width: orbSize, height: orbSize, top: orbTop, left: orbLeft }}
        aria-hidden
      >
        <SessionMascot size={orbSize} compact={orbCompact} />
      </div>

      {/* Outer white slide frame */}
      <div className="relative rounded-[24px] overflow-hidden bg-white border border-slate-200 shadow-[0_18px_36px_-16px_rgba(15,23,42,0.22)] p-3 sm:p-3.5">
        {/* Slide surface (16:10 keeps focus on session) */}
        <div className="relative rounded-2xl overflow-hidden bg-slate-900 aspect-[16/10]">
          <AnimatePresence mode="wait">
            <motion.div
              key={industry.id + "-scene"}
              initial={{ opacity: 0, scale: 1.03 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0"
            >
              {/* Poster as baseline */}
              <img
                src={f.image}
                alt={f.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Autoplay muted looping video (open-source from Pexels) */}
              {f.video && (
                <video
                  src={f.video}
                  poster={f.image}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              )}
            </motion.div>
          </AnimatePresence>

          {/* Bottom: scene title */}
          <div className="absolute inset-x-3 bottom-3 z-10">
            <AnimatePresence mode="wait">
              <motion.div
                key={industry.id + "-title"}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.5 }}
                className="inline-block bg-black/45 backdrop-blur-md text-[12px] text-white px-2.5 py-1 rounded-md shadow-sm ring-1 ring-white/15"
              >
                {f.title}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Trainee REC overlay (bottom-right, ~28% width) */}
          <TraineeRec />
        </div>

        {/* Live AI caption panel (types per industry) */}
        <SessionCaption industry={industry} />
      </div>
    </div>
  );
}

/* Trainee recording preview — mimics a live-session camera tile:
 *   • portrait thumbnail
 *   • REC badge (pulsing red dot)
 *   • animated audio-level bars
 *   • "TRAINEE · MM:SS" footer
 */
function TraineeRec() {
  return (
    <div
      className="absolute right-2.5 bottom-3 z-10 w-[25%] max-w-[190px] rounded-xl overflow-hidden border border-white/40 shadow-[0_14px_36px_-12px_rgba(0,0,0,0.6)] bg-slate-800"
      data-testid="trainee-rec"
    >
      <div className="relative aspect-[2.5/2]">
        <img
          src={traineeAvatar}
          alt="Trainee"
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        {/* REC badge */}
        <div className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 text-[8.5px] font-black uppercase tracking-[0.18em] text-white bg-rose-500/95 backdrop-blur px-1.5 py-0.5 rounded-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
          REC
        </div>
        {/* Audio-level bars */}
        <div className="absolute top-1.5 right-1.5 flex items-end gap-[2px] h-3">
          <span className="w-[2px] h-[55%] bg-emerald-300 rounded-full voice-wave-bar" />
          <span className="w-[2px] h-[85%] bg-emerald-300 rounded-full voice-wave-bar" style={{ animationDelay: "0.15s" }} />
          <span className="w-[2px] h-[70%] bg-emerald-300 rounded-full voice-wave-bar" style={{ animationDelay: "0.3s" }} />
          <span className="w-[2px] h-[100%] bg-emerald-300 rounded-full voice-wave-bar" style={{ animationDelay: "0.45s" }} />
        </div>
      </div>
      <div className="rounded-b-xl px-2 py-1 bg-slate-900/90 backdrop-blur flex items-center justify-between">
        <span className="text-[9.5px] font-bold uppercase tracking-wider text-white">
          Trainee
        </span>
        <span className="text-[9.5px] font-semibold text-slate-300 tabular-nums">
          12:34
        </span>
      </div>
    </div>
  );
}

/* Compact SpeakingMascot — 3 pulsing rings around a small MascotOrb.
 * `compact` tightens halo + rings for mobile (FeaturedModuleCard). */
function SessionMascot({ size = 76, compact = false }) {
  const haloInset = compact ? -5 : -8;
  const ringInset = compact ? 4 : 6;

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      {/* Violet halo */}
      <span
        className="absolute rounded-full pointer-events-none"
        style={{
          inset: haloInset,
          background:
            "radial-gradient(circle, rgba(139,92,246,0.42), rgba(139,92,246,0) 65%)",
        }}
      />
      {/* Pulsing rings */}
      <span className="tutor-speak-ring absolute rounded-full" style={{ inset: ringInset }} />
      <span
        className="tutor-speak-ring absolute rounded-full"
        style={{ inset: ringInset, animationDelay: "0.6s" }}
      />
      <span
        className="tutor-speak-ring absolute rounded-full"
        style={{ inset: ringInset, animationDelay: "1.2s" }}
      />
      <div className="relative z-10">
        <MascotOrb size={Math.round(size * 0.7)} />
      </div>
    </div>
  );
}

/* Word-by-word typing caption (a visual proxy for live AI speech).
 * Resets whenever the industry changes so the caption re-types per scene.
 */
function SessionCaption({ industry }) {
  const script = industry.portal.featured.caption || "";
  const words = script.split(" ");
  const [wordCount, setWordCount] = useState(0);

  useEffect(() => {
    setWordCount(0);
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setWordCount(i);
      if (i >= words.length) clearInterval(id);
    }, 120);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industry.id]);

  const shown = words.slice(0, wordCount).join(" ");
  const done = wordCount >= words.length;

  return (
    <div className="relative mt-3 rounded-xl bg-slate-50/80 border border-slate-100 px-3 py-2.5 ">
      <div className="flex items-center gap-2">
        <div className="flex items-end gap-[2px] h-3">
          <span className="w-[2px] h-full bg-violet-500 rounded-full voice-wave-bar" />
          <span
            className="w-[2px] h-full bg-violet-500 rounded-full voice-wave-bar"
            style={{ animationDelay: "0.15s" }}
          />
          <span
            className="w-[2px] h-full bg-violet-500 rounded-full voice-wave-bar"
            style={{ animationDelay: "0.3s" }}
          />
          <span
            className="w-[2px] h-full bg-violet-500 rounded-full voice-wave-bar"
            style={{ animationDelay: "0.45s" }}
          />
        </div>
        <span className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-violet-700">
          AI tutor is speaking
        </span>
      </div>
      <div
        className="mt-1.5 text-[12.5px] text-slate-800 leading-snug min-h-[3em]"
        data-testid={`session-caption-${industry.id}`}
      >
        {shown}
        {!done && (
          <span
            className="inline-block w-[2px] h-[1em] align-[-2px] bg-violet-500 ml-0.5"
            style={{ animation: "voiceWave 1s step-end infinite" }}
          />
        )}
      </div>
    </div>
  );
}

/* Unused since sessions grid was removed */
